/**
 * ProductTile — shared tile for the Home tab's favourites and
 * recently-purchased carousels, plus the top carousel on the
 * /favourites page (DEV-46921).
 *
 * The tile runs in two modes, discriminated by whether the caller
 * passes a `timestampLabel`:
 *
 *   - **Favourite mode** (no timestamp): image, name, supplier only,
 *     price. Matches the mockup — no branch, no distance, no stock
 *     pill. Kept intentionally minimal so the tile stays about a
 *     saved product, not a live shop.
 *
 *   - **Recently-purchased mode** (timestamp given): image, name,
 *     supplier · distance, stock pill, price + timestamp. Matches
 *     the mockup's Recently-Purchased carousel.
 *
 * The tile itself is a button — tapping it navigates to the master
 * or supplier-product detail page depending on the favourite target
 * type. The heart is a nested button that stops propagation so the
 * tile navigation doesn't fire when toggling.
 */

import { Wrench, Heart, Clock } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { ProductViewSource } from '../../lib/analytics'
import type { BestListing } from '../../types'
import { useFavourites } from '../../auth/hooks/useFavourites'
import { useFavouriteToggle } from './favourites/useFavouriteToggle'
import { IN_STOCK_LABEL } from '../../constants/display'

export interface ProductTileProps {
  listing: BestListing
  /** Present only on recently-purchased tiles. Also acts as the mode
   * switch: with a timestamp we render the full metadata (distance +
   * stock pill + stamp); without it we render the minimal favourite
   * tile (supplier + price only, matching the mockup). */
  timestampLabel?: string
  /** Analytics source to attribute the product_viewed event to. The tile is
   * shared across carousels, so the parent says where it lives (a favourites
   * carousel passes 'favourite'); omitted leaves the view as 'direct'. */
  viewSource?: ProductViewSource
}

function StockPill({ status }: { status: BestListing['stockStatus'] }) {
  if (status === 'in_stock') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-green-600">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        {IN_STOCK_LABEL}
      </span>
    )
  }
  if (status === 'low_stock') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-600">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        Low stock
      </span>
    )
  }
  if (status === 'out_of_stock') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-red-600">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        Out of stock
      </span>
    )
  }
  // checking / unknown
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-400">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
      Checking…
    </span>
  )
}

export function ProductTile({ listing, timestampLabel, viewSource }: ProductTileProps) {
  const navigate = useNavigate()
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const { isFavourited } = useFavourites()
  const toggleFavourite = useFavouriteToggle()

  // A tile is either a master target (opens /engineer/product/{id})
  // or a raw target (opens /engineer/supplier-product/{id}). The
  // backend already tells us which via targetType — we don't guess.
  const target =
    listing.targetType === 'raw' && listing.rawProductId != null
      ? { type: 'raw' as const, id: listing.rawProductId }
      : listing.masterProductId != null
        ? { type: 'master' as const, id: listing.masterProductId }
        : null
  const favourited = target ? isFavourited(target) : false

  const openDetail = () => {
    if (!target) return
    const navState = viewSource ? { state: { source: viewSource } } : undefined
    if (target.type === 'raw') {
      // Preserve branchId as a query param when known — the supplier
      // detail page uses it to preselect the tile's branch.
      const suffix = listing.branchId ? `?branchId=${listing.branchId}` : ''
      navigate(
        `/${tenantSlug}/engineer/supplier-product/${target.id}${suffix}`,
        navState,
      )
    } else {
      navigate(`/${tenantSlug}/engineer/product/${target.id}`, navState)
    }
  }

  // Fixed-width carousel tile (matches the mockup — the tile is only
  // used inside `overflow-x-auto` snap carousels on Home and the top
  // of /favourites). The dedicated list pages use their own compact
  // row layout, so we don't need a full-width variant.
  const containerClasses =
    'relative flex-shrink-0 w-60 snap-start bg-white rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all overflow-hidden'

  // Two rendering modes discriminated by `timestampLabel`:
  //   - recently-purchased mode (isRecentlyPurchased=true): full meta
  //     — supplier · distance + stock pill + timestamp.
  //   - favourite mode (else): supplier only, no distance, no stock
  //     pill, no timestamp. Matches the mockup.
  const isRecentlyPurchased = timestampLabel != null && timestampLabel !== ''

  const supplierLine = (() => {
    if (!listing.supplierName) return null
    if (isRecentlyPurchased && listing.distanceMiles != null) {
      return `${listing.supplierName} · ${listing.distanceMiles.toFixed(1)} mi`
    }
    return listing.supplierName
  })()

  return (
    <div className={containerClasses}>
      <button
        onClick={openDetail}
        className="w-full text-left"
        aria-label={`Open ${listing.displayName ?? 'product'} detail`}
      >
        <div className="w-full bg-gray-100 h-28 flex items-center justify-center overflow-hidden">
          {listing.imageUrl ? (
            // eslint-disable-next-line @next/next/no-img-element -- Vite app, not Next
            <img
              src={listing.imageUrl}
              alt={listing.displayName ?? 'Product image'}
              className="w-full h-full object-contain"
            />
          ) : (
            <Wrench className="w-10 h-10 text-gray-300" />
          )}
        </div>

        <div className="p-4">
          <h3 className="text-gray-900 font-bold text-sm mb-2 line-clamp-2">
            {listing.displayName ?? 'Unnamed product'}
          </h3>

          {/* Supplier line + inline stock pill. In recently-purchased
           * mode the stock pill sits on the SAME line as the supplier
           * text (e.g. "Wolseley · 0.0 mi · ● In Stock"). In favourite
           * mode only the supplier name renders (no stock, no distance). */}
          {(supplierLine || isRecentlyPurchased) && (
            <div className="flex flex-wrap items-center gap-x-1.5 text-gray-500 text-xs mb-2">
              {supplierLine && <span className="line-clamp-1">{supplierLine}</span>}
              {isRecentlyPurchased && (
                <>
                  {supplierLine && <span className="text-gray-300">·</span>}
                  <StockPill status={listing.stockStatus} />
                </>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-brand-primary font-bold">
              {listing.price != null ? `£${listing.price.toFixed(2)}` : '—'}
            </span>
            {isRecentlyPurchased && (
              <div className="flex items-center gap-1 text-gray-400 text-xs">
                <Clock className="w-3 h-3" />
                <span>{timestampLabel}</span>
              </div>
            )}
          </div>
        </div>
      </button>

      {target && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            toggleFavourite({ target, productName: listing.displayName ?? 'Unnamed product' })
          }}
          // The ::before extends the tap area to 44px without changing the look.
          className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm hover:bg-white transition-colors before:absolute before:-inset-2 before:content-['']"
          aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={favourited}
        >
          <Heart
            className={`w-4 h-4 ${favourited ? 'text-brand-primary' : 'text-gray-400'}`}
            fill={favourited ? 'currentColor' : 'none'}
          />
        </button>
      )}
    </div>
  )
}
